import React, { useEffect, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Fade, Modal, Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { FormInput, EFormInputTypes } from 'components/molecules/FormInput';
import { useTranslations } from 'hooks/useTranslations';
import { colors } from 'theme/colors';
import { FormModal } from 'components/molecules/Modal';
import { EAlertVariants } from 'components/atoms/Alert';
import { ModalContent, ButtonContainer } from './styles';

interface IFormValues {
  internalReason?: string;
  externalReason?: string;
}

interface ILicenceActionModalProps {
  open: boolean;
  title: string;
  message: string;
  name?: string;
  onClickClose: () => void;
  onClickApprove: (internalReason?: string, externalReason?: string) => void;
  approveButtonVariant?: EAlertVariants.success | EAlertVariants.error;
  approveButtonLabel: string;
  showDualReasonFields?: boolean;
  cancelButtonLabel?: string;
  areYouSureModal?: boolean;
}

const initialFormValues: IFormValues = { internalReason: '', externalReason: '' };

export const LicenceActionModal = ({
  open,
  title,
  message,
  name,
  onClickClose,
  onClickApprove,
  approveButtonVariant,
  approveButtonLabel,
  showDualReasonFields = false,
  cancelButtonLabel,
  areYouSureModal = false,
}: ILicenceActionModalProps) => {
  const { t } = useTranslations();
  const [areYouSureModalOpen, setAreYouSureModalOpen] = useState<boolean>(
    areYouSureModal ? false : open,
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!areYouSureModal) {
      setAreYouSureModalOpen(open);
    }
  }, [open]);

  const schema = yup.object({
    internalReason: showDualReasonFields
      ? yup
          .string()
          .required(t('general.field.validation.required'))
          .max(1000, t('general.field.validation.maxCharacters').replace('{0}', '1000'))
      : yup.string().optional(),
    externalReason: showDualReasonFields
      ? yup
          .string()
          .required(t('general.field.validation.required'))
          .max(1000, t('general.field.validation.maxCharacters').replace('{0}', '1000'))
      : yup.string().optional(),
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<IFormValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IFormValues>,
  });

  const handleClose = () => {
    reset(initialFormValues);
    setAreYouSureModalOpen(false);
    onClickClose();
  };

  const handleApprove = async (data: IFormValues) => {
    setIsLoading(true);
    try {
      if (showDualReasonFields) {
        await onClickApprove(data.internalReason, data.externalReason);
      } else {
        await onClickApprove();
      }
      setAreYouSureModalOpen(false);
      reset(initialFormValues);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        aria-labelledby="licence-action-modal-title"
        aria-describedby="licence-action-modal-description"
        open={areYouSureModalOpen}
        onClose={handleClose}
        closeAfterTransition
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'center',
          padding: { xs: '0', sm: '20px' },
          '& .MuiBackdrop-root': {
            backgroundColor: colors.modalBackdrop,
          },
        }}
      >
        <Fade in={open}>
          <ModalContent isCompact={!showDualReasonFields}>
            <Typography
              variant="h5"
              sx={{
                color: colors.primary500,
                textAlign: 'left',
                margin: 0,
                marginBottom: showDualReasonFields ? '12px' : '8px',
              }}
            >
              {title}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                textAlign: 'left',
                margin: 0,
                marginBottom: showDualReasonFields ? '32px' : '20px',
              }}
            >
              {message}
            </Typography>

            {showDualReasonFields && (
              <>
                <div style={{ position: 'relative', marginBottom: '16px' }}>
                  <FormInput
                    name="internalReason"
                    type={EFormInputTypes.textarea}
                    minRows={3}
                    multiline
                    label={`${t('licenceActionModal.internalReason.label')}*`}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    charsLimit={1000}
                    showCounter={true}
                  />
                </div>
                <div style={{ position: 'relative' }}>
                  <FormInput
                    name="externalReason"
                    type={EFormInputTypes.textarea}
                    minRows={3}
                    multiline
                    label={`${t('licenceActionModal.externalReason.label')}*`}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    charsLimit={1000}
                    showCounter={true}
                  />
                </div>
              </>
            )}

            <ButtonContainer isCompact={!showDualReasonFields}>
              <Button
                id="legalEntity_licence_modalCancel-button"
                label={cancelButtonLabel || t('licenceActionModal.cancel.label')}
                variant={EButtonVariants.outlined}
                size={EButtonSizes.small}
                onClick={handleClose}
                disabled={isLoading}
                type="button"
                fullWidth
              />
              <Button
                id="legalEntity_licence_modalApprove-button"
                label={approveButtonLabel}
                size={EButtonSizes.small}
                styleVariant={approveButtonVariant}
                variant={EButtonVariants.contained}
                onClick={handleSubmit(handleApprove)}
                isLoading={isLoading}
                type="button"
                fullWidth
              />
            </ButtonContainer>
          </ModalContent>
        </Fade>
      </Modal>
      {areYouSureModal && (
        <FormModal
          open={open}
          onClickClose={onClickClose}
          onClickApprove={() => setAreYouSureModalOpen(true)}
          title={title}
          titleLeft
          text={t('licenceActionModal.areYouSureText.label').replace(
            '{0}',
            name?.toLowerCase() ?? '',
          )}
          approveButtonLabel="general.yes.label"
          cancelButtonLabel="general.no.label"
        />
      )}
    </>
  );
};
